import React, { useState, useMemo, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { toN, normalizeSimpleActuals, fmtDiff } from "../utils";
import { Card, ST } from "../ui/SharedUI";

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
}

export default function Dashboard({revenue, expenses, expenseItems, expOverride, staff, actuals, months, taxNote, diffComments, onChangeDiffComment}) {
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth <= 768;
  const isSmall  = windowWidth <= 400;
  const simple = useMemo(() => normalizeSimpleActuals(actuals.simple, months), [actuals.simple, months]);
  const {
    planRev, planExp, planProf, actRev, actExp, chartData, filled,
    revisedRevTotal, revisedExpTotal, revisedProfTotal, planRevTotal, planExpTotal, planProfTotal,
    revRevDiff, revExpDiff, revProfDiff
  } = useMemo(() => {
    const autoVals = {
      salary:    Object.fromEntries(months.map(m=>[m, staff.reduce((s,p)=>s+toN(p.salary[m]),0)])),
      bonus:     Object.fromEntries(months.map(m=>[m, staff.reduce((s,p)=>s+toN(p.bonus[m]),0)])),
      transport: Object.fromEntries(months.map(m=>[m, staff.reduce((s,p)=>s+toN(p.transport[m]),0)])),
    };
    const itemById = Object.fromEntries(expenseItems.map(item => [item.id, item]));
    const getPlannedExpense = (id,m) => {
      const item = itemById[id];
      if (item?.auto && !expOverride[id]) return toN(autoVals[item.autoKey]?.[m]);
      return toN(expenses[id]?.[m]);
    };
    const planRev  = months.map(m=>revenue.reduce((s,r)=>s+toN(r.monthly[m]),0));
    const planExp  = months.map(m=>expenseItems.reduce((s,item)=>s+getPlannedExpense(item.id,m),0));
    const planProf = months.map((_,i)=>planRev[i]-planExp[i]);
    const actRev = months.map(m=>{
      const hasDetail = revenue.some(r=>(actuals.revItems?.[r.id]?.[m]??"")!=="");
      if (hasDetail) return revenue.reduce((s,r)=>s+toN(actuals.revItems?.[r.id]?.[m]),0);
      const simpleHas = simple.revItems?.some(item => (item.monthly?.[m] ?? "") !== "");
      if (simpleHas) return simple.revItems.reduce((s,item)=>s+toN(item.monthly?.[m]),0);
      return null;
    });
    const actExp = months.map(m=>{
      const hasDetail = actuals.items && expenseItems.some(item=>(actuals.items[item.id]?.[m]??"")!=="");
      if (hasDetail) return expenseItems.reduce((s,item)=>s+toN(actuals.items[item.id]?.[m]),0);
      const simpleHas = simple.expItems?.some(item => (item.monthly?.[m] ?? "") !== "");
      if (simpleHas) return simple.expItems.reduce((s,item)=>s+toN(item.monthly?.[m]),0);
      return null;
    });
    const chartData= months.map((m,i)=>({name:m, 予測利益:planProf[i], 実績利益:actRev[i]!=null&&actExp[i]!=null?actRev[i]-actExp[i]:null}));
    const filled = months.filter((_,i)=>actRev[i]!==null || actExp[i]!==null);
    const revisedRev = months.map((m,i) => actRev[i] != null ? actRev[i] : planRev[i]);
    const revisedExp = months.map((m,i) => actExp[i] != null ? actExp[i] : planExp[i]);
    const revisedProf = months.map((_,i) => revisedRev[i] - revisedExp[i]);
    const revisedRevTotal = revisedRev.reduce((s,v)=>s+v,0);
    const revisedExpTotal = revisedExp.reduce((s,v)=>s+v,0);
    const revisedProfTotal = revisedProf.reduce((s,v)=>s+v,0);
    const planRevTotal = planRev.reduce((s,v)=>s+v,0);
    const planExpTotal = planExp.reduce((s,v)=>s+v,0);
    const planProfTotal = planProf.reduce((s,v)=>s+v,0);
    const revExpDiff = revisedExpTotal - planExpTotal;
    return { planRev, planExp, planProf, actRev, actExp, chartData, filled, revisedRevTotal, revisedExpTotal, revisedProfTotal, planRevTotal, planExpTotal, planProfTotal, revRevDiff: revisedRevTotal - planRevTotal, revExpDiff, revProfDiff: revisedProfTotal - planProfTotal };
  }, [months, revenue, expenses, expenseItems, expOverride, staff, actuals, simple]);

  const [commentMonth, setCommentMonth] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const openCommentForMonth = (m) => { setCommentMonth(m); setCommentDraft(diffComments[m] ?? ""); };
  useEffect(() => {
    if (!commentMonth) return;
    const onKeyDown = (e) => { if (e.key === "Escape") setCommentMonth(null); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [commentMonth]);

  const kpis = [
    { label: filled.length > 0 ? "修正見込み売上" : "年間予測売上", value: revisedRevTotal, sub: `${fmtDiff(revRevDiff, true)?.text || "+0"} vs 予測（予測: ${planRevTotal.toLocaleString()}）`, subColor: fmtDiff(revRevDiff, true)?.color || "#888", note: `実績${filled.length}ヶ月 + 予測${12-filled.length}ヶ月` },
    { label: filled.length > 0 ? "修正見込み費用" : "年間予測費用", value: filled.length > 0 ? revisedExpTotal : planExpTotal, sub: filled.length > 0 ? `${fmtDiff(revExpDiff, false)?.text || "+0"} vs 予測（予測: ${planExpTotal.toLocaleString()}）` : "予測ベース", subColor: fmtDiff(revExpDiff, false)?.color || "#888", note: filled.length > 0 ? `実績${filled.length}ヶ月 + 予測${12-filled.length}ヶ月` : "年間予測費用合計" },
    { label: filled.length > 0 ? "修正見込み利益" : "年間予測利益", value: revisedProfTotal, sub: `${fmtDiff(revProfDiff, true)?.text || "+0"} vs 予測（予測: ${planProfTotal.toLocaleString()}）`, subColor: fmtDiff(revProfDiff, true)?.color || "#888", note: `実績${filled.length}ヶ月 + 予測${12-filled.length}ヶ月` },
    { label: "実績入力済み", value: `${filled.length} / 12`, sub: `${(filled.length/12*100).toFixed(0)}%`, subColor: "#888", note: "" },
  ];

  const PRED_C = "#7DB87D"; const ACT_C = "#2D7A2D"; const REV_C = "#2255AA";

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"16px"}}>
      <div style={{background:"#FFFBF0",border:"1px solid #E8D8A0",borderLeft:"4px solid #AA7722",borderRadius:"8px",padding:"14px 16px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"8px"}}><span style={{fontSize:"11px",fontWeight:"700",color:"#AA7722",fontFamily:"'Noto Sans JP',sans-serif",letterSpacing:"0.03em"}}>税理士短評</span></div>
        <div style={{minHeight:"60px",padding:"8px 10px",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif",lineHeight:"1.7",color:taxNote?"#3A2000":"#bbb",background:"#FFFEF8",border:"1px solid #E8D8A0",borderRadius:"6px",whiteSpace:"pre-wrap",wordBreak:"break-all"}}>{taxNote||"税理士からの短評はまだありません。"}</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:isSmall?"1fr":isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:isMobile?"8px":"12px"}}>
        {kpis.map(k=>(<Card key={k.label} style={{textAlign:"center"}}><div style={{fontSize:"11px",color:"#888",marginBottom:"4px",fontFamily:"'Noto Sans JP',sans-serif"}}>{k.label}</div><div style={{fontSize:isMobile?"18px":"22px",fontWeight:"800",color:"#1A3A1A",fontFamily:"monospace"}}>{typeof k.value==="number"?k.value.toLocaleString():k.value}</div><div style={{fontSize:"11px",color:k.subColor,marginTop:"6px",fontWeight:"700"}}>{k.sub}</div><div style={{fontSize:"10px",color:"#aaa",marginTop:"2px"}}>{k.note}</div></Card>))}
      </div>
      <Card>
        <ST>月次利益 予測 vs 実績</ST>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} barGap={2}>
            <XAxis dataKey="name" tick={{fontSize:11,fill:"#888"}} axisLine={false} tickLine={false}/>
            <YAxis tick={{fontSize:11,fill:"#888"}} axisLine={false} tickLine={false} tickFormatter={v=>`${(v/10000).toFixed(0)}万`}/>
            <Tooltip formatter={(v,n)=>[v?.toLocaleString()+" 円",n]}/>
            <Bar dataKey="予測利益" fill="#C8E6C8" radius={[3,3,0,0]}/>
            <Bar dataKey="実績利益" fill="#2D7A2D" radius={[3,3,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </Card>
      <Card>
        <ST>月次予実サマリ</ST>
        {(()=>{
          const revisedRev  = months.map((m,i) => actRev[i]  != null ? actRev[i]  : planRev[i]);
          const revisedExp  = months.map((m,i) => actExp[i]  != null ? actExp[i]  : planExp[i]);
          const actProf  = months.map((m,i) => (actRev[i]!=null && actExp[i]!=null) ? actRev[i]-actExp[i] : null);
          const revisedProf = months.map((_,i) => revisedRev[i] - revisedExp[i]);
          const totPlanRev=planRev.reduce((s,v)=>s+v,0); const totPlanExp=planExp.reduce((s,v)=>s+v,0); const totPlanProf=planProf.reduce((s,v)=>s+v,0);
          const totActRev=actRev.reduce((s,v)=>s+(v??0),0); const totActExp=actExp.reduce((s,v)=>s+(v??0),0); const totActProf=actProf.reduce((s,v)=>s+(v??0),0);
          const totRevRev=revisedRev.reduce((s,v)=>s+v,0); const totRevExp=revisedExp.reduce((s,v)=>s+v,0); const totRevProf=revisedProf.reduce((s,v)=>s+v,0);
          const rows = [
            {label:"予測売上",color:PRED_C,bg:"#F5FAF5",vals:planRev,sum:totPlanRev,isDiff:false},
            {label:"予測費用",color:PRED_C,bg:"#F0F8F0",vals:planExp,sum:totPlanExp,isDiff:false},
            {label:"予測利益",color:PRED_C,bg:"#EBF5EB",vals:planProf,sum:totPlanProf,isDiff:false},
            {label:"実績売上",color:ACT_C,bg:"#FAFCFA",vals:actRev,sum:totActRev,isDiff:false,spacer:true},
            {label:"実績費用",color:ACT_C,bg:"#F5FAF5",vals:actExp,sum:totActExp,isDiff:false},
            {label:"実績利益",color:ACT_C,bg:"#EEF7EE",vals:actProf,sum:totActProf,isDiff:false},
            {label:"予実売上",color:REV_C,bg:"#F0F5FF",vals:revisedRev,sum:totRevRev,isDiff:false,spacer:true},
            {label:"予実費用",color:REV_C,bg:"#EBF0FF",vals:revisedExp,sum:totRevExp,isDiff:false},
            {label:"予実利益",color:REV_C,bg:"#E5EEFF",vals:revisedProf,sum:totRevProf,isDiff:false},
          ];
          return (
            <div style={{overflowX:"auto"}}>
              <table className="summary-table" style={{width:"100%",borderCollapse:"collapse",fontSize:"12px",fontFamily:"monospace"}}>
                <thead>
                  <tr style={{borderBottom:"2px solid #E8EDE8"}}>
                    <th style={{textAlign:"left",padding:"5px 8px",color:"#888",fontWeight:"600",minWidth:"110px",fontFamily:"'Noto Sans JP',sans-serif"}}>項目</th>
                    {months.map(m=><th key={m} onClick={()=>openCommentForMonth(m)} style={{textAlign:"right",padding:"5px 4px",color:"#888",fontWeight:"600",minWidth:"72px",cursor:"pointer"}}>{m}{diffComments[m]?"✏️":""}</th>)}
                    <th style={{textAlign:"right",padding:"5px 8px",color:"#888",fontWeight:"600",minWidth:"80px",position:"sticky",right:0,background:"#fff",boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>合計</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row)=>(
                    <React.Fragment key={row.label}>
                      {row.spacer && <tr><td colSpan={months.length+2} style={{height:"10px",padding:"0",background:"transparent"}}></td></tr>}
                      <tr style={{background:row.bg}}>
                        <td style={{padding:"5px 8px",color:row.color,fontWeight:"700",fontFamily:"'Noto Sans JP',sans-serif",fontSize:"12px"}}>{row.label}</td>
                        {row.vals.map((v,i)=><td key={i} style={{padding:"5px 4px",textAlign:"right",color:v!=null?row.color:"#ccc",fontWeight:"600"}}>{v!=null?v.toLocaleString():"—"}</td>)}
                        <td style={{padding:"5px 8px",textAlign:"right",fontWeight:"800",color:row.color,position:"sticky",right:0,background:row.bg,boxShadow:"-2px 0 4px rgba(0,0,0,0.06)"}}>{row.sum.toLocaleString()}</td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </Card>
      {commentMonth && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}} onClick={()=>setCommentMonth(null)}>
          <div style={{width:"min(560px,92vw)",background:"#fff",borderRadius:"12px",padding:"18px",boxShadow:"0 20px 40px rgba(0,0,0,0.18)"}} onClick={(e)=>e.stopPropagation()}>
            <div style={{fontSize:"14px",fontWeight:"800",color:"#1A3A1A",marginBottom:"10px",fontFamily:"'Noto Sans JP',sans-serif"}}>{commentMonth} の差異理由メモ</div>
            <textarea value={commentDraft} onChange={(e)=>setCommentDraft(e.target.value)} placeholder="差異の理由や対応方針を入力" rows={6} style={{width:"100%",boxSizing:"border-box",border:"1px solid #D0D8D0",borderRadius:"8px",padding:"10px 12px",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif",lineHeight:"1.7",resize:"vertical"}}/>
            <div style={{display:"flex",justifyContent:"flex-end",gap:"8px",marginTop:"12px"}}>
              <button onClick={()=>setCommentMonth(null)} style={{padding:"7px 14px",border:"1px solid #D0D8D0",borderRadius:"6px",background:"#fff",cursor:"pointer"}}>閉じる</button>
              <button onClick={()=>{onChangeDiffComment(commentMonth, commentDraft); setCommentMonth(null);}} style={{padding:"7px 14px",border:"none",borderRadius:"6px",background:"#2D7A2D",color:"#fff",cursor:"pointer",fontWeight:"700"}}>保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
